import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { getFactionColor, type FactionId } from '@shared/data/factions';
import { ENTITY_NAME_FONT_FAMILY } from '@shared/constants';
import { PARTY_NAME_COLOR } from '@shared/data/party';

/**
 * Labels de nombre sobre entidades (players + NPCs comparten `addPlayerNameText`): creación con
 * sombra estilo Argentum, color por facción y posicionamiento. Extraído de GameScene (god-object).
 * El estado de render (playerObjects/npcObjects) y el chokepoint de fuente (getSelectedPlayerNamesFont)
 * viven en GameScene (host) y se acceden vía `this.scene.*`. NB: 12px, no tocar tamaño (ver memoria).
 */
export class EntityLabelController {
  constructor(private scene: GameScene) {}

  public getLabelShadow(label: Phaser.GameObjects.BitmapText): Phaser.GameObjects.BitmapText | undefined {
    return label.getData('shadow') as Phaser.GameObjects.BitmapText | undefined;
  }

  public destroyEntityLabel(label: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText): void {
    if (label instanceof Phaser.GameObjects.BitmapText) {
      const shadow = this.getLabelShadow(label);
      if (shadow) {
        shadow.destroy();
      }
    }

    label.destroy();
  }

  public setEntityLabelPosition(
    label: Phaser.GameObjects.Text | Phaser.GameObjects.BitmapText,
    x: number,
    y: number
  ): void {
    // Use float positions - Phaser's roundPixels:true handles final rendering
    label.setPosition(x, y);
  }

  /**
   * Crea un texto para nombre de personaje usando Google Fonts configurable
   * Incluye sombra estilo Argentum (1px derecha, 1px abajo) para legibilidad
   */
  /**
   * Color del nombre de un player: color de party (si es miembro del propio
   * grupo) o color de facción (vacío/newbie = blanco). Ver docs/PARTY_SYSTEM.md.
   */
  public labelColorForPlayerId(id: string): string {
    if (this.scene.partyController?.isPartyMember(id)) return PARTY_NAME_COLOR;
    const faction = this.scene.room?.state.players.get(id)?.faction ?? '';
    return getFactionColor(faction as FactionId);
  }

  /** Recolorea los nombres de todos los players (tras un cambio de party). */
  public refreshPlayerNameColors(): void {
    for (const [id, render] of this.scene.playerObjects) {
      const lbl = render.label;
      if (lbl instanceof Phaser.GameObjects.Text) lbl.setColor(this.labelColorForPlayerId(id));
    }
  }

  public addPlayerNameText(
    x: number,
    y: number,
    text: string,
    depth?: number,
    color: string = '#ffffff'
  ): Phaser.GameObjects.Text {
    // Los nombres usan Pixeloid (ENTITY_NAME_FONT_FAMILY), separado del resto del
    // texto in-game (que va en Monogram vía getSelectedPlayerNamesFont).
    const fontFamily = ENTITY_NAME_FONT_FAMILY;
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily,
      fontSize: '12px',
      // Sin bold: Monogram (pixel font) tiene un solo peso; el faux-bold la arruina.
      color,
      resolution: 2,
      // Sombra estilo Argentum: 2px derecha, 2px abajo, negro, sin blur.
      shadow: {
        offsetX: 2,
        offsetY: 2,
        color: '#000000',
        blur: 0,
        fill: true,
        stroke: false,
      },
    };
    const label = this.scene.add.text(x, y, text, style);
    label.setOrigin(0.5, 0);
    if (typeof depth === 'number') {
      label.setDepth(depth);
    }
    return label;
  }


  public updateLabelShadowDepth(label: Phaser.GameObjects.BitmapText, depth: number): void {
    const shadow = this.getLabelShadow(label);

    if (shadow) {
      shadow.setDepth(depth);
    }
  }

}
